import { FunctionComponent } from 'react';
import Markdown from 'markdown-to-jsx';
import dynamic from 'next/dynamic';
import SyntaxHighlighter from 'react-syntax-highlighter';
import shadesOfPurple from 'react-syntax-highlighter/dist/cjs/styles/hljs/shades-of-purple';

const MarkdownImage = dynamic(() => import('./MarkdownImage'));
import Course from '@playground/components/Course';

interface Props {
  content: string;
  ctaLink: string;
  ctaText: string;
  showCourse: boolean;
}

const Guide: FunctionComponent<Props> = ({ content, ctaLink, ctaText, showCourse }) => {
  return (
    <div className="h-full focus:outline-0 focus:ring-0 flex flex-col flex-auto space-y-3">
      <div className="flex-auto h-full overflow-y-auto scroll-smooth scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-gray-900 pb-4 pr-3">
        <Markdown
          options={{
            overrides: {
              img: {
                component: MarkdownImage,
              },
              ul: {
                props: {
                  className: 'text-sm list-disc list-outside px-4 mb-4',
                },
              },
              ol: {
                props: {
                  className: 'text-sm list-decimal list-outside px-4 mb-4',
                },
              },
              li: {
                props: {
                  className: 'text-sm font-light text-gray-300 mt-0 mb-1 leading-6',
                },
              },
              strong: {
                props: {
                  className: 'text-sm text-gray-300 font-normal',
                },
              },
              p: {
                component: ({ children, ...props }) => {
                  const PComponent = children[0]?.type === MarkdownImage ? 'div' : 'p';
                  return <PComponent {...props}>{children}</PComponent>;
                },
                props: {
                  className: 'text-sm font-light text-gray-300 leading-6 mb-4 pr-4',
                },
              },
              pre: {
                component: ({ children, ...props }) => <p {...props}>{children}</p>,
                props: {
                  className: 'my-4 pr-4',
                },
              },
              code: {
                component: ({ className, children }) => {
                  if (!className?.includes('lang')) {
                    return <code className="p-0.5 bg-white/10 rounded">{children}</code>;
                  }
                  const language = (className || '')
                    .replace('lang-', '')
                    .replace('my-4 text-xs', '');
                  return (
                    <SyntaxHighlighter
                      className={className}
                      language={language}
                      style={shadesOfPurple}
                    >
                      {children}
                    </SyntaxHighlighter>
                  );
                },
                props: {
                  className: 'my-4 text-xs',
                },
              },
              blockquote: {
                props: {
                  className: 'relative border-l-2 pl-4',
                },
              },
              a: {
                props: {
                  className: 'underline text-sky-500 decoration-sky-500',
                },
              },
              table: {
                component: ({ children }) => (
                  <div
                    className="overflow-x-auto scrollbar-thin scrollbar-thumb-slate-700
                  scrollbar-track-gray-900 mb-2"
                  >
                    <table className="font-mono text-sm table-fixed box-border border-collapse border mb-2">
                      {children}
                    </table>
                  </div>
                ),
              },
              th: {
                props: {
                  className: 'border border-slate-600 text-left p-1 min-w-[5rem]',
                },
              },
              td: {
                props: {
                  className: 'border border-slate-600 p-1',
                },
              },
            },
          }}
        >
          {content}
        </Markdown>
      </div>
      {showCourse && <Course course={ctaText} link={ctaLink} />}
    </div>
  );
};

export default Guide;
